import { Fragment, useEffect, useRef } from 'react';

import Stack from '@mui/material/Stack';

import useValidateResetToken from '@webclient/components/user-authentication/hooks/useValidateResetToken';
import {
  StyledBox,
  StyledTextField,
} from '@webclient/components/user-authentication/sections/steps/components/pin-code/styles';

import { Controller, UseFormReturn } from 'react-hook-form';

export type ValidateCodeFormType = {
  code: string[];
};

const CODE_LENGTH = 6;

export const defaultValues = {
  code: Array(CODE_LENGTH).fill(''),
};

type Props = UseFormReturn<ValidateCodeFormType>;

export default function RHFPinCode({
  control,
  setValue,
  watch,
  getValues,
}: Props) {
  const { isPending: isValidatePending } = useValidateResetToken();

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(function () {
    inputRefs.current = inputRefs.current.slice(0, CODE_LENGTH);
  }, []);

  function handleKeyDown(
    index: number,
    event: React.KeyboardEvent<HTMLDivElement>,
  ) {
    const value = watch(`code.${index}`);

    if (event.key === 'Backspace') {
      event.preventDefault();

      if (value) {
        setValue(`code.${index}`, '');
      } else if (index > 0) {
        setValue(`code.${index - 1}`, '');
        inputRefs.current[index - 1]?.focus();
      }
    }
  }

  function handleInputChange(
    index: number,
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) {
    const value = event.target.value.slice(-1);

    if (value) {
      setValue(`code.${index}`, value);
      if (index < CODE_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  }

  function handlePaste(event: React.ClipboardEvent, focusedIndex: number) {
    event.preventDefault();
    const pastedData = event.clipboardData.getData('text');
    const currentValues = getValues('code');

    //? Keep values before focused index, then add pasted chars
    const updatedValues = [
      ...currentValues.slice(0, focusedIndex),
      ...pastedData.split(''),
      ...currentValues.slice(focusedIndex + pastedData.length),
    ].slice(0, CODE_LENGTH);

    updatedValues.forEach((value, index) => {
      setValue(`code.${index}`, value);
    });

    const lastIndex = Math.min(
      focusedIndex + pastedData.length - 1,
      CODE_LENGTH - 1,
    );

    inputRefs.current[lastIndex]?.focus();
  }

  return (
    <Stack
      gap={3}
      direction="row"
      alignItems="center"
    >
      {defaultValues.code.map((_, index) => (
        <Fragment key={index}>
          {index === 3 && <StyledBox />}
          <Controller
            name={`code.${index}`}
            control={control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                placeholder="0"
                disabled={isValidatePending}
                inputRef={(el) => (inputRefs.current[index] = el)}
                onChange={(e) => handleInputChange(index, e)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={(e) => handlePaste(e, index)}
              />
            )}
          />
        </Fragment>
      ))}
    </Stack>
  );
}
